import {
  PayloadAction,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import _ from "lodash";
import { RootState } from "../../app/store";
import { Config } from "../../config/Config";
import { EverywhereErrorCodes } from "../../config/EverywhereErrorCodes";
import { EverywhereSuccessCodes } from "../../config/EverywhereSuccessCodes";
import { SmartDatePlan } from "../Smartplan/SmartDatePlan/SmartDatePlanSlice";
import { SmartPlan } from "../Smartplan/SmartPlanSlice";
import { UserInfo } from "../UserInfo/UserSlice";
import { WellnessBody } from "../Wellness/WellnessSlice/WellnessBodySlice";
import { WellnessNutritional } from "../Wellness/WellnessSlice/WellnessNutritionalSlice";
import { WellnessPsycho } from "../Wellness/WellnessSlice/WellnessPsychoSlice";
import { MasterClassDetail } from "../masterclass/MasterClassSlice";
import MyOrdersRepository from "./MyOrdersRepository";

export interface MyOrderDetail {
  id: number;
  bookingDate: Date;
  numberOfPeople: number;
  status: number;
  purchasedBy: number;
  tenantId: number;
  userInfo: UserInfo[];
  smartPlan?: SmartPlan;
  smartPlanDate?: SmartDatePlan;
  masterClass?: MasterClassDetail;
  wellnessBody?: WellnessBody;
  wellnessNutritional?: WellnessNutritional;
  wellnessPsycho?: WellnessPsycho;
  prenotazione?: boolean; // ← AGGIUNGI QUESTO CAMPO
}

export const getOrderAsync = createAsyncThunk(
  "myOrder/getOrder",
  async (tenantId: number) => {
    const myOrdersRepository = new MyOrdersRepository();
    const response = await myOrdersRepository.getOrderById(tenantId);
    const order = _.get(response, Config.MY_ORDER_RESPONSE);
    return order;
  }
);

// ✨ AGGIUNGI QUESTO ASYNC THUNK
export const getBookingByIdAsync = createAsyncThunk(
  "myOrder/getBookingById",
  async (bookingId: number) => {
    const myOrdersRepository = new MyOrdersRepository();
    const response = await myOrdersRepository.getBookingById(bookingId);
    return response.data; // Questo conterrà tutto il booking incluso il campo prenotazione
  }
);

export const checkBookingExistsAsync = createAsyncThunk(
  "myOrder/checkBookingExists",
  async (bookingId: number) => {
    const myOrdersRepository = new MyOrdersRepository();
    const response = await myOrdersRepository.checkBookingExists(bookingId);
    return {
      bookingId: bookingId,
      exists: response.data,
    };
  }
);

export const createAssociationAsync = createAsyncThunk(
  "myOrder/createAssocation",
  async (data: { bookingId: number; queryString: string }) => {
    const myOrdersRepository = new MyOrdersRepository();
    const response = await myOrdersRepository.createAssociation(
      data.bookingId,
      data.queryString
    );
    const order = _.get(response, Config.MY_ORDER_RESPONSE);
    return order;
  }
);

export const updateBookingPrenotazioneAsync = createAsyncThunk(
  "myOrder/updateBookingPrenotazione",
  async (data: { bookingId: number; prenotazione: boolean }) => {
    const myOrdersRepository = new MyOrdersRepository();
    const response = await myOrdersRepository.updateBookingPrenotazione(
      data.bookingId,
      data.prenotazione
    );
    return {
      bookingId: data.bookingId,
      prenotazione: data.prenotazione,
      response: response.data,
    };
  }
);

export const deleteAssociationAsync = createAsyncThunk(
  "myOrder/deleteAssociation",
  async (data: { bookingId: number; queryString: string }) => {
    const myOrdersRepository = new MyOrdersRepository();
    const response = await myOrdersRepository.deleteAssociation(
      data.bookingId,
      data.queryString
    );
    const order = _.get(response, Config.MY_ORDER_RESPONSE);
    return order;
  }
);

//#region Slice

const myOrdersAdapter = createEntityAdapter<MyOrderDetail>({
  selectId: (myOrder) => myOrder.id,
});

export const myOrderDetailsSlice = createSlice({
  name: "myOrderDetails",
  initialState: myOrdersAdapter.getInitialState({
    status: "idle",
    reasonCode: "",
    elementOrdered: 0,
    currentBooking: null, // ← AGGIUNGI QUESTO
    bookingExistsResult: null, // ← AGGIUNGI QUESTO SE NON C'È
  }),
  reducers: {
    myOrdersEmptyState: (state) => {
      myOrdersAdapter.setAll(state, []);
      state.reasonCode = "";
      state.status = "idle";
      state.elementOrdered = 0;
      state.currentBooking = null; // ← AGGIUNGI QUESTO
      state.bookingExistsResult = null; // ← AGGIUNGI QUESTO
    },
    myOrdersElementOrderedEmptyState: (state) => {
      state.elementOrdered = 0;
      state.reasonCode = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getOrderAsync.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          myOrdersAdapter.upsertOne(state, action.payload);
          state.status = "idle";
          state.reasonCode = EverywhereSuccessCodes.GET;
        }
      )
      .addCase(getOrderAsync.pending, (state: any) => {
        state.status = "loading";
      })
      .addCase(getOrderAsync.rejected, (state: any) => {
        state.status = "failed";
        state.reasonCode = EverywhereErrorCodes.INTERNAL_SERVER_ERROR;
      })
      // ✨ AGGIUNGI QUESTI CASE PER getBookingByIdAsync
      .addCase(
        getBookingByIdAsync.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.status = "idle";
          state.reasonCode = EverywhereSuccessCodes.GET;
          state.currentBooking = action.payload; // Salva il booking corrente
        }
      )
      .addCase(getBookingByIdAsync.pending, (state: any) => {
        state.status = "loading";
      })
      .addCase(getBookingByIdAsync.rejected, (state: any) => {
        state.status = "failed";
        state.reasonCode = EverywhereErrorCodes.INTERNAL_SERVER_ERROR;
      })
      .addCase(
        createAssociationAsync.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          myOrdersAdapter.setOne(state, action.payload);
          state.status = "idle";
          state.reasonCode = EverywhereSuccessCodes.POST;
        }
      )
      .addCase(createAssociationAsync.pending, (state: any) => {
        state.status = "loading";
      })
      .addCase(createAssociationAsync.rejected, (state: any) => {
        state.status = "failed";
        state.reasonCode = EverywhereErrorCodes.INTERNAL_SERVER_ERROR;
      })
      .addCase(
        deleteAssociationAsync.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          myOrdersAdapter.setOne(state, action.payload);
          state.status = "idle";
          state.reasonCode = EverywhereSuccessCodes.DELETE;
        }
      )
      .addCase(deleteAssociationAsync.pending, (state: any) => {
        state.status = "loading";
      })
      .addCase(deleteAssociationAsync.rejected, (state: any) => {
        state.status = "failed";
        state.reasonCode = EverywhereErrorCodes.INTERNAL_SERVER_ERROR;
      })
      .addCase(
        checkBookingExistsAsync.fulfilled,
        (
          state: any,
          action: PayloadAction<{ bookingId: number; exists: boolean }>
        ) => {
          state.status = "idle";
          state.reasonCode = EverywhereSuccessCodes.GET;
          state.bookingExistsResult = action.payload;
        }
      )
      .addCase(checkBookingExistsAsync.pending, (state: any) => {
        state.status = "loading";
      })
      .addCase(checkBookingExistsAsync.rejected, (state: any) => {
        state.status = "failed";
        state.reasonCode = EverywhereErrorCodes.INTERNAL_SERVER_ERROR;
      })
      .addCase(
        updateBookingPrenotazioneAsync.fulfilled,
        (
          state: any,
          action: PayloadAction<{ bookingId: number; prenotazione: boolean }>
        ) => {
          state.status = "idle";
          state.reasonCode = EverywhereSuccessCodes.PUT;
          // Aggiorna il booking nello stato se necessario
          const booking = state.entities[action.payload.bookingId];
          if (booking) {
            booking.prenotazione = action.payload.prenotazione;
          }
          // ✨ AGGIORNA ANCHE IL currentBooking SE È LO STESSO
          if (
            state.currentBooking &&
            state.currentBooking.id === action.payload.bookingId
          ) {
            state.currentBooking.prenotazione = action.payload.prenotazione;
          }
        }
      )
      .addCase(updateBookingPrenotazioneAsync.pending, (state: any) => {
        state.status = "loading";
      })
      .addCase(updateBookingPrenotazioneAsync.rejected, (state: any) => {
        state.status = "failed";
        state.reasonCode = EverywhereErrorCodes.INTERNAL_SERVER_ERROR;
      });
  },
});

//#endRegion Slice

//#region Status

export const myOrderDetailSelector = myOrdersAdapter.getSelectors<RootState>(
  (state) => state.myOrderDetail
);

export const { myOrdersEmptyState } = myOrderDetailsSlice.actions;
export const selectMyOrdersSliceStatus = (state: any) => state.myOrders.status;
export const selectMyOrdersSliceReasonCode = (state: any) =>
  state.myOrders.reasonCode;
export const selectBookingExistsResult = (state: any) =>
  state.myOrderDetail.bookingExistsResult;

// ✨ AGGIUNGI QUESTO SELECTOR
export const selectCurrentBooking = (state: any) =>
  state.myOrderDetail.currentBooking;

//#endRegion Status

export default myOrderDetailsSlice.reducer;
